import { upperFirst } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';

import styles from './VisitCard.module.scss';

export const STATUS_WORD = {
  readiness: { green: 'Ready', yellow: 'At risk', red: 'Not ready', neutral: 'No tasks' },
  followUp: { green: 'Complete', yellow: 'In progress', red: 'Stalled', neutral: 'No tasks' },
};

export const visitOpenLabel = (member, visitLabel) =>
  `Open ${member || 'this member'}'s ${/\bvisit$/i.test(visitLabel) ? visitLabel : `${visitLabel} visit`}`;

export const VisitCard = ({
  status = 'neutral',
  selected = false,
  onOpen,
  openLabel,
  children,
}) => (
  <div
    data-visit-card=""
    className={[
      styles.card,
      status === 'green' ? styles.ready : styles.plain,
      selected ? styles.selected : '',
    ]
      .filter(Boolean)
      .join(' ')}
  >
    <button
      type="button"
      data-card-open=""
      className={styles.opener}
      aria-label={openLabel}
      aria-current={selected ? 'true' : undefined}
      onClick={onOpen}
    />
    <div className={styles.content}>{children}</div>
  </div>
);

const SQUARE_ORDER = { green: 0, canceled: 1, yellow: 2, red: 3 };

export const ReadinessSquares = ({ blocks, done, total, children }) => {
  const ordered = [...blocks].sort((a, b) => SQUARE_ORDER[a.status] - SQUARE_ORDER[b.status]);
  return (
    <span className={styles.squaresRow}>
      <span className={styles.squares} aria-hidden="true">
        {ordered.map((block) => (
          <span
            key={block.id}
            className={`${styles.square} ${styles[`square${upperFirst(block.status)}`]}`}
          />
        ))}
      </span>
      <span className={styles.count}>{`${done}/${total}`}</span>
      {children}
    </span>
  );
};

export const ProgressLine = ({ done, total, status = 'green', className }) => {
  const pct = total > 0 ? (done / total) * 100 : 100;
  return (
    <span className={[styles.progress, className].filter(Boolean).join(' ')}>
      <span
        className={styles.track}
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={done}
        aria-label={`${done} of ${total} done`}
      >
        <span
          className={`${styles.fill} ${styles[`fill${upperFirst(status)}`]}`}
          style={{ width: `${pct}%` }}
        />
      </span>
      <span className={`${styles.progressWords} ${done === total ? styles.allDone : ''}`}>
        {`${done}/${total} done`}
      </span>
    </span>
  );
};

VisitCard.propTypes = {
  status: PropTypes.oneOf(['green', 'yellow', 'red', 'neutral']),
  selected: PropTypes.bool,
  onOpen: PropTypes.func,
  openLabel: PropTypes.string,
  children: PropTypes.node,
};

ReadinessSquares.propTypes = {
  blocks: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]) }),
  ).isRequired,
  done: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  children: PropTypes.node,
};

ProgressLine.propTypes = {
  done: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  status: PropTypes.oneOf(['green', 'yellow', 'red', 'neutral']),
  className: PropTypes.string,
};
